import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { aiAPI } from '../../../services/api';
import { parseUTCTimestamp } from '../../../utils/dateUtils';

interface AgentLogEntry {
  id: string;
  content: string;
  created_time: string | null;
  cleared_time: string | null;
}

function formatAge(isoString: string | null): string {
  if (!isoString) return '';
  const created = parseUTCTimestamp(isoString);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  return 'just now';
}

export const AgentLogViewer: React.FC = () => {
  const queryClient = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['agent-log'],
    queryFn: () => aiAPI.getAgentLog({ include_cleared: true, days: 7 }),
  });

  const clearMutation = useMutation({
    mutationFn: (entryId: string) => aiAPI.clearAgentLogEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-log'] });
    },
  });

  const entries = data?.entries ?? [];
  const activeEntries = entries.filter((e: AgentLogEntry) => !e.cleared_time);
  const clearedEntries = entries.filter((e: AgentLogEntry) => e.cleared_time);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Agent Log
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Short-term scratchpad used by the AI agent across sessions. Questions, observations, and reminders.
      </p>

      {isLoading ? (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center">
          Loading...
        </div>
      ) : activeEntries.length === 0 ? (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center italic">
          No active log entries. The agent will add entries here as it works across sessions.
        </div>
      ) : (
        <div className="space-y-2">
          {activeEntries.map((entry: AgentLogEntry) => (
            <div
              key={entry.id}
              className="flex items-start gap-2 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md"
            >
              <div className="flex-1 min-w-0">
                <p className="text-gray-700 dark:text-gray-300">{entry.content}</p>
                <div className="flex gap-2 mt-1 text-xs text-gray-400 dark:text-gray-500">
                  <span>{formatAge(entry.created_time)}</span>
                </div>
              </div>
              <button
                onClick={() => clearMutation.mutate(entry.id)}
                disabled={clearMutation.isPending}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                title="Clear entry"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Recent History */}
      {clearedEntries.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            {showHistory ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Recent history ({clearedEntries.length})
          </button>

          {showHistory && (
            <div className="mt-2 space-y-1.5">
              {clearedEntries.map((entry: AgentLogEntry) => (
                <div
                  key={entry.id}
                  className="px-3 py-1.5 text-sm bg-gray-50/50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-md"
                >
                  <p className="text-gray-400 dark:text-gray-500 line-through">{entry.content}</p>
                  <div className="flex gap-2 mt-0.5 text-xs text-gray-300 dark:text-gray-600">
                    <span>{formatAge(entry.created_time)}</span>
                    {entry.cleared_time && (
                      <span>cleared {formatAge(entry.cleared_time)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
